import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Users, X, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Star, Check, Download, Loader2 } from 'lucide-react';
import {
  getClinicLocationClientAccounts,
  updateClinicLocationClientAccount,
  setPrimaryLocationClinic,
  createLocationAccountNote,
  exportClinicLocationClientAccounts,
} from '../services/clientClinicApi';
import AsyncContent from '@shared/components/common/AsyncContent';
import { ServiceUseTypeLabels } from '../types/clientClinic';
import type { ClinicLocationClientAccount, AccountStatus } from '../types/clientClinic';
import RequirePermission from '@shared/components/RequirePermission';
import './ClientClinicRelationshipsPage.css';
import { withXmId } from '@shared/utils/xmId';

// Status Badge Component
function StatusBadge({ status }: { status: AccountStatus }) {
  const className = `status-badge status-${status.toLowerCase()}`;
  return <span className={className}>{status}</span>;
}

/**
 * SLV2-2919: one grid row per (customer location, clinic) relationship — the
 * Clinical Services and Injury Management records are merged into a single row
 * with per-service-type flag columns instead of one row per service type.
 * The flattened fields exist so the generic column filter/sort can address
 * them by key.
 */
interface MergedRelationship {
  key: string;
  clinical?: ClinicLocationClientAccount;
  injuryMgmt?: ClinicLocationClientAccount;
  customerName: string;
  customerXmId?: string | null;
  customerLocationName: string;
  clinicName: string;
  clinicCode: string;
  clinicAddress: string;
  clinicCity: string;
  clinicState: string;
  clinicZip: string;
  clinicPhone: string;
  /** Display label list, e.g. "Clinical Services, Injury Management" — filterable text. */
  services: string;
  csPreferred: boolean;
  csPrimary: boolean;
  imPreferred: boolean;
  imPrimary: boolean;
  imPanelState: boolean;
  accountNumber: string;
  accountStatus: string;
  notes: string;
}

function mergeRelationships(accounts: ClinicLocationClientAccount[]): MergedRelationship[] {
  const byPair = new Map<string, MergedRelationship>();
  for (const account of accounts) {
    const key = `${account.customerLocationId}|${account.clinicId}`;
    let row = byPair.get(key);
    if (!row) {
      row = {
        key,
        customerName: account.customerName || '',
        customerXmId: account.customerXmId,
        customerLocationName: account.customerLocationName || '',
        clinicName: account.clinicName || '',
        clinicCode: account.clinicCode || '',
        clinicAddress: account.clinicAddress || '',
        clinicCity: account.clinicCity || '',
        clinicState: account.clinicState || '',
        clinicZip: account.clinicZip || '',
        clinicPhone: account.clinicPhone || '',
        services: '',
        csPreferred: false,
        csPrimary: false,
        imPreferred: false,
        imPrimary: false,
        imPanelState: false,
        accountNumber: '',
        accountStatus: '',
        notes: '',
      };
      byPair.set(key, row);
    }
    if (account.serviceTypeKey === 'InjuryMgmt') {
      row.injuryMgmt = account;
      row.imPreferred = account.isPreferred;
      row.imPrimary = account.isPrimary;
      row.imPanelState = account.isPanelState;
    } else {
      row.clinical = account;
      row.csPreferred = account.isPreferred;
      row.csPrimary = account.isPrimary;
    }
  }
  for (const row of byPair.values()) {
    const records = [row.clinical, row.injuryMgmt].filter(Boolean) as ClinicLocationClientAccount[];
    row.services = records.map((r) => ServiceUseTypeLabels[r.serviceTypeKey] || r.serviceTypeKey).join(', ');
    row.accountNumber = [...new Set(records.map((r) => r.accountNumber).filter(Boolean))].join(' / ');
    row.accountStatus = [...new Set(records.map((r) => r.accountStatus))].join(' / ');
    row.notes = [...new Set(records.map((r) => r.notes).filter(Boolean))].join(' | ');
  }
  return [...byPair.values()];
}

// Sort/Filter types
type SortDirection = 'asc' | 'desc' | null;
type SortConfig = { key: string; direction: SortDirection };
type FilterKind = 'text' | 'yesno';

// Sortable Column Header Component
interface SortableHeaderProps {
  label: string;
  sortKey: string;
  sortConfig: SortConfig;
  onSort: (key: string) => void;
  filterValue: string;
  onFilterChange: (value: string) => void;
  filterKind?: FilterKind;
}

function SortableHeader({ label, sortKey, sortConfig, onSort, filterValue, onFilterChange, filterKind = 'text' }: Readonly<SortableHeaderProps>) {
  const isActive = sortConfig.key === sortKey;
  return (
    <th>
      <div className="th-content">
        <div className="th-sort-row" onClick={() => onSort(sortKey)}>
          <span className="th-label">{label}</span>
          <span className={`sort-chevrons ${isActive ? 'active' : ''}`}>
            <ChevronUp size={12} className={`sort-icon ${isActive && sortConfig.direction === 'asc' ? 'active' : ''}`} />
            <ChevronDown size={12} className={`sort-icon ${isActive && sortConfig.direction === 'desc' ? 'active' : ''}`} />
          </span>
        </div>
        {filterKind === 'yesno' ? (
          <select
            className="column-filter"
            aria-label={`Filter ${label}`}
            value={filterValue}
            onChange={(e) => onFilterChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          >
            <option value="">All</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        ) : (
          <input
            type="text"
            className="column-filter"
            placeholder="Search..."
            aria-label={`Filter ${label}`}
            value={filterValue}
            onChange={(e) => onFilterChange(e.target.value)}
            onClick={(e) => e.stopPropagation()}
          />
        )}
      </div>
    </th>
  );
}

function getNestedValue(obj: Record<string, unknown>, key: string): string {
  const val = obj[key];
  if (val == null) return '';
  return String(val);
}

// Main Page Component
export default function ClientClinicRelationshipsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>('');
  const [deleteTarget, setDeleteTarget] = useState<{
    records: ClinicLocationClientAccount[];
    name: string;
  } | null>(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [setPrimaryTarget, setSetPrimaryTarget] = useState<ClinicLocationClientAccount | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 50;

  // Sort & column filter state
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: '', direction: null });
  const [columnFilters, setColumnFilters] = useState<Record<string, string>>({});

  // Excel export state
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState<string | null>(null);

  const handleSort = useCallback((key: string) => {
    setSortConfig((prev) => {
      if (prev.key !== key) return { key, direction: 'asc' };
      if (prev.direction === 'asc') return { key, direction: 'desc' };
      return { key: '', direction: null };
    });
  }, []);

  const handleColumnFilter = useCallback((key: string, value: string) => {
    setColumnFilters((prev) => ({ ...prev, [key]: value }));
    setCurrentPage(1);
  }, []);

  // Reset sort/filters on filter change
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    setSortConfig({ key: '', direction: null });
    setColumnFilters({});
    setCurrentPage(1);
  }, [statusFilter]);
  /* eslint-enable react-hooks/set-state-in-effect */

  // Query — always fetch every service type: rows merge into one line per
  // (location, clinic) pair, so a server-side service-type filter would drop
  // the other half of the pair. The service-type dropdown filters client-side.
  const {
    data: locationAccounts = [],
    isLoading: isLoadingData,
    error: queryError,
  } = useQuery({
    queryKey: ['clinicLocationClientAccounts', statusFilter],
    queryFn: () => getClinicLocationClientAccounts(statusFilter || undefined),
  });

  // Set Primary mutation
  const setPrimaryMutation = useMutation({
    mutationFn: (id: number) => setPrimaryLocationClinic(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinicLocationClientAccounts'] });
      setSetPrimaryTarget(null);
    },
  });

  // Inactivate mutation — a merged row inactivates every non-inactive record
  // in the pair (both service types), each with the same reason note.
  const inactivateMutation = useMutation({
    mutationFn: async ({ records, notes }: { records: ClinicLocationClientAccount[]; notes: string }) => {
      for (const record of records) {
        await updateClinicLocationClientAccount(record.id, { accountStatus: 'Inactive', notes });
        await createLocationAccountNote(record.id, { noteText: notes, noteType: 'Inactivation' });
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['clinicLocationClientAccounts'] });
      setDeleteTarget(null);
      setDeleteReason('');
    },
  });

  const handleRowClick = (row: MergedRelationship) => {
    const target = row.clinical ?? row.injuryMgmt;
    if (target) navigate(`/client-clinic-relationships/${target.id}`);
  };

  const handleDelete = (e: React.MouseEvent, row: MergedRelationship) => {
    e.stopPropagation();
    const records = [row.clinical, row.injuryMgmt]
      .filter((r): r is ClinicLocationClientAccount => !!r && r.accountStatus !== 'Inactive');
    if (records.length === 0) return;
    const name = `${row.customerLocationName} - ${row.clinicName}`;
    setDeleteTarget({ records, name });
    setDeleteReason('');
  };

  const handleStarClick = (e: React.MouseEvent, account: ClinicLocationClientAccount) => {
    e.stopPropagation();
    if (!account.isPrimary) {
      setSetPrimaryTarget(account);
    }
  };

  const confirmDelete = () => {
    if (!deleteTarget || !deleteReason.trim()) return;
    const notes = `[Inactivated] ${deleteReason.trim()}`;
    inactivateMutation.mutate({ records: deleteTarget.records, notes });
  };

  // One row per (location, clinic) pair — SLV2-2919
  const mergedRows = useMemo(() => mergeRelationships(locationAccounts), [locationAccounts]);

  // Boolean columns filter by exact 'true'/'false' match; text columns by contains
  const yesNoKeys = useMemo(
    () => new Set(['csPreferred', 'csPrimary', 'imPreferred', 'imPrimary', 'imPanelState']),
    []
  );

  // Filter and sort merged rows
  const filteredRows = useMemo(() => {
    let data = [...mergedRows];
    if (serviceTypeFilter === 'Clinical') data = data.filter((r) => !!r.clinical);
    if (serviceTypeFilter === 'InjuryMgmt') data = data.filter((r) => !!r.injuryMgmt);
    Object.entries(columnFilters).forEach(([key, value]) => {
      if (!value) return;
      const lower = value.toLowerCase();
      data = data.filter((item) => {
        const val = getNestedValue(item as unknown as Record<string, unknown>, key);
        return yesNoKeys.has(key) ? val === value : val.toLowerCase().includes(lower);
      });
    });
    if (sortConfig.key && sortConfig.direction) {
      data.sort((a, b) => {
        const aVal = getNestedValue(a as unknown as Record<string, unknown>, sortConfig.key);
        const bVal = getNestedValue(b as unknown as Record<string, unknown>, sortConfig.key);
        const cmp = aVal.localeCompare(bVal, undefined, { sensitivity: 'base' });
        return sortConfig.direction === 'asc' ? cmp : -cmp;
      });
    }
    return data;
  }, [mergedRows, serviceTypeFilter, columnFilters, sortConfig, yesNoKeys]);

  // Reset page when filters/sort change the result set
  const totalItems = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const safePage = Math.min(currentPage, totalPages);
  const paginatedData = filteredRows.slice((safePage - 1) * pageSize, safePage * pageSize);

  // Column definitions with minimum widths so content is never truncated
  const locationColumns: { key: string; label: string; minWidth: string; filterKind?: FilterKind }[] = [
    { key: 'customerName', label: 'Customer', minWidth: '180px' },
    { key: 'customerLocationName', label: 'Location', minWidth: '170px' },
    { key: 'clinicName', label: 'Clinic Name', minWidth: '200px' },
    { key: 'clinicCode', label: 'Clinic Code', minWidth: '100px' },
    { key: 'clinicAddress', label: 'Address', minWidth: '180px' },
    { key: 'clinicCity', label: 'City', minWidth: '120px' },
    { key: 'clinicState', label: 'State', minWidth: '70px' },
    { key: 'clinicZip', label: 'Zip', minWidth: '90px' },
    { key: 'clinicPhone', label: 'Phone', minWidth: '130px' },
    { key: 'services', label: 'Services', minWidth: '150px' },
    { key: 'csPreferred', label: 'CS Preferred', minWidth: '90px', filterKind: 'yesno' },
    { key: 'csPrimary', label: 'CS Primary', minWidth: '90px', filterKind: 'yesno' },
    { key: 'imPreferred', label: 'IM Preferred', minWidth: '90px', filterKind: 'yesno' },
    { key: 'imPrimary', label: 'IM Primary', minWidth: '90px', filterKind: 'yesno' },
    { key: 'imPanelState', label: 'IM Panel State', minWidth: '100px', filterKind: 'yesno' },
    { key: 'accountNumber', label: 'Account #', minWidth: '130px' },
    { key: 'accountStatus', label: 'Status', minWidth: '100px' },
    { key: 'notes', label: 'Notes', minWidth: '200px' },
  ];

  // Export the current filtered + sorted view (all matching rows, ignoring
  // pagination). The backend export is per-record, so post the underlying
  // records of every visible merged row.
  const handleExport = useCallback(async () => {
    setIsExporting(true);
    setExportError(null);
    try {
      const records = filteredRows.flatMap((row) =>
        [row.clinical, row.injuryMgmt].filter((r): r is ClinicLocationClientAccount => !!r)
      );
      const blob = await exportClinicLocationClientAccounts(records);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `ClientClinicRelationships_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err: unknown) {
      setExportError(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setIsExporting(false);
    }
  }, [filteredRows]);

  // Preferred check + primary star pair for one service-type record
  const renderPreferredCell = (record?: ClinicLocationClientAccount) => {
    if (!record) return <td className="cell-muted">—</td>;
    return (
      <td>
        {record.isPreferred ? (
          <Check size={18} className="preferred-icon" />
        ) : (
          <Check size={18} className="preferred-icon-empty" />
        )}
      </td>
    );
  };

  const renderPrimaryCell = (record?: ClinicLocationClientAccount) => {
    if (!record) return <td className="cell-muted">—</td>;
    return (
      <td>
        {record.isPreferred ? (
          <button
            type="button"
            className={`star-btn ${record.isPrimary ? 'star-primary' : 'star-preferred'}`}
            onClick={(e) => handleStarClick(e, record)}
            title={record.isPrimary ? 'Primary clinic' : 'Set as primary'}
          >
            <Star size={18} fill={record.isPrimary ? '#f59e0b' : 'none'} />
          </button>
        ) : null}
      </td>
    );
  };

  return (
    <div className="client-clinic-page">
      <div className="page-header">
        <div>
          <div className="page-title-row">
            <Users size={26} />
            <h1>Client-Clinic Relationships</h1>
          </div>
          <p className="page-subtitle">Manage clinic assignments and coverage across customer locations</p>
        </div>
        <RequirePermission permission="UserCanCreateClinicRelationship">
          <button className="btn-primary add-btn" onClick={() => navigate('/client-clinic-relationships/new')}>
            <Plus size={18} />
            Add Relationship
          </button>
        </RequirePermission>
      </div>

      <div className="tabs-container">
        <div className="filters">
          <select
            value={serviceTypeFilter}
            onChange={(e) => setServiceTypeFilter(e.target.value)}
            className="status-filter"
            aria-label="Filter by service type"
          >
            <option value="">All Service Types</option>
            <option value="Clinical">Has Clinical Services</option>
            <option value="InjuryMgmt">Has Injury Management</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="status-filter"
            aria-label="Filter by status"
          >
            <option value="">All Statuses</option>
            <option value="Pending">Pending</option>
            <option value="Active">Active</option>
            <option value="Inactive">Inactive</option>
          </select>
          <button
            type="button"
            className="btn-secondary export-btn"
            onClick={handleExport}
            disabled={isExporting || filteredRows.length === 0}
            title="Export the current filtered list to Excel"
          >
            {isExporting ? (
              <>
                <Loader2 size={16} className="spinner" />
                Generating...
              </>
            ) : (
              <>
                <Download size={16} />
                Export to Excel
              </>
            )}
          </button>
        </div>
      </div>
      {exportError && (
        <div className="export-error" role="alert">{exportError}</div>
      )}

      <div className="table-container">
        <AsyncContent
          loading={isLoadingData}
          error={queryError ? 'Error loading relationships' : null}
          loadingMessage="Loading relationships..."
        >
          <table className="data-table">
            <colgroup>
              {locationColumns.map((col) => (
                <col key={col.key} style={{ minWidth: col.minWidth }} />
              ))}
              <col style={{ minWidth: '80px' }} />
            </colgroup>
            <thead>
              <tr>
                {locationColumns.map((col) => (
                  <SortableHeader
                    key={col.key}
                    label={col.label}
                    sortKey={col.key}
                    sortConfig={sortConfig}
                    onSort={handleSort}
                    filterValue={columnFilters[col.key] || ''}
                    onFilterChange={(v) => handleColumnFilter(col.key, v)}
                    filterKind={col.filterKind}
                  />
                ))}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedData.length === 0 ? (
                <tr>
                  <td colSpan={locationColumns.length + 1} className="empty-message">
                    No location-level relationships found
                  </td>
                </tr>
              ) : (
                paginatedData.map((row) => (
                  <tr
                    key={row.key}
                    onClick={() => handleRowClick(row)}
                    className="clickable-row"
                  >
                    <td className="cell-primary">{withXmId(row.customerName, row.customerXmId)}</td>
                    <td className="cell-secondary">{row.customerLocationName}</td>
                    <td className="cell-primary">{row.clinicName}</td>
                    <td className="cell-muted">{row.clinicCode}</td>
                    <td className="cell-secondary">{row.clinicAddress || '-'}</td>
                    <td className="cell-secondary">{row.clinicCity || '-'}</td>
                    <td className="cell-secondary">{row.clinicState || '-'}</td>
                    <td className="cell-secondary">{row.clinicZip || '-'}</td>
                    <td className="cell-secondary">{row.clinicPhone || '-'}</td>
                    <td>
                      {[row.clinical, row.injuryMgmt]
                        .filter((r): r is ClinicLocationClientAccount => !!r)
                        .map((record) => (
                          <button
                            key={record.id}
                            type="button"
                            className={`service-type-badge service-type-${record.serviceTypeKey.toLowerCase()} service-type-link`}
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/client-clinic-relationships/${record.id}`);
                            }}
                            title={`Open the ${ServiceUseTypeLabels[record.serviceTypeKey]} relationship`}
                          >
                            {ServiceUseTypeLabels[record.serviceTypeKey]}
                          </button>
                        ))}
                    </td>
                    {renderPreferredCell(row.clinical)}
                    {renderPrimaryCell(row.clinical)}
                    {renderPreferredCell(row.injuryMgmt)}
                    {renderPrimaryCell(row.injuryMgmt)}
                    <td>
                      {row.injuryMgmt ? (
                        row.imPanelState ? (
                          <Check size={18} className="preferred-icon" />
                        ) : (
                          <Check size={18} className="preferred-icon-empty" />
                        )
                      ) : (
                        <span className="cell-muted">—</span>
                      )}
                    </td>
                    <td className="cell-secondary">{row.accountNumber || '-'}</td>
                    <td>
                      {[...new Set(
                        [row.clinical, row.injuryMgmt]
                          .filter((r): r is ClinicLocationClientAccount => !!r)
                          .map((r) => r.accountStatus)
                      )].map((status) => (
                        <StatusBadge key={status} status={status} />
                      ))}
                    </td>
                    <td className="notes-cell">{row.notes || '-'}</td>
                    <td className="actions-cell">
                      <RequirePermission permission="UserCanDeleteClinicRelationship">
                        {[row.clinical, row.injuryMgmt].some((r) => r && r.accountStatus !== 'Inactive') && (
                          <button
                            className="icon-btn delete"
                            onClick={(e) => handleDelete(e, row)}
                            title="Inactivate"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </RequirePermission>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </AsyncContent>
      </div>

      {totalItems > 0 && (
        <div className="pagination-bar">
          <span className="pagination-info">
            Showing {((safePage - 1) * pageSize) + 1}–{Math.min(safePage * pageSize, totalItems)} of {totalItems.toLocaleString()}
          </span>
          <div className="pagination-controls">
            <button
              type="button"
              className="pagination-btn"
              onClick={() => setCurrentPage(1)}
              disabled={safePage <= 1}
              title="First page"
            >
              First
            </button>
            <button
              type="button"
              className="pagination-btn"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={safePage <= 1}
              title="Previous page"
            >
              <ChevronLeft size={14} />
            </button>
            <span className="pagination-page">
              Page {safePage} of {totalPages}
            </span>
            <button
              type="button"
              className="pagination-btn"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={safePage >= totalPages}
              title="Next page"
            >
              <ChevronRight size={14} />
            </button>
            <button
              type="button"
              className="pagination-btn"
              onClick={() => setCurrentPage(totalPages)}
              disabled={safePage >= totalPages}
              title="Last page"
            >
              Last
            </button>
          </div>
        </div>
      )}

      {deleteTarget && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget && !inactivateMutation.isPending) {
            setDeleteTarget(null);
            setDeleteReason('');
          }
        }}>
          <div className="modal">
            <div className="modal-header">
              <h2>Inactivate Relationship</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => { setDeleteTarget(null); setDeleteReason(''); }}
                disabled={inactivateMutation.isPending}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>Are you sure you want to inactivate the relationship <strong>{deleteTarget.name}</strong>?</p>
              {deleteTarget.records.length > 1 && (
                <p>
                  This inactivates both service types (
                  {deleteTarget.records.map((r) => ServiceUseTypeLabels[r.serviceTypeKey]).join(' and ')}
                  ) for this relationship.
                </p>
              )}
              <div className="form-group">
                <label htmlFor="delete-reason">
                  Reason for inactivation<span className="required">*</span>
                </label>
                <textarea
                  id="delete-reason"
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  placeholder="Enter the reason this relationship is being inactivated..."
                  rows={3}
                  required
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => { setDeleteTarget(null); setDeleteReason(''); }}
                disabled={inactivateMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmDelete}
                disabled={inactivateMutation.isPending || !deleteReason.trim()}
              >
                {inactivateMutation.isPending ? 'Saving...' : 'Inactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {setPrimaryTarget && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target === e.currentTarget && !setPrimaryMutation.isPending) {
            setSetPrimaryTarget(null);
          }
        }}>
          <div className="modal">
            <div className="modal-header">
              <h2>Set Primary Clinic</h2>
              <button
                type="button"
                className="modal-close"
                onClick={() => setSetPrimaryTarget(null)}
                disabled={setPrimaryMutation.isPending}
                aria-label="Close modal"
              >
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p>
                Set <strong>{setPrimaryTarget.clinicName}</strong> as the primary{' '}
                {(ServiceUseTypeLabels[setPrimaryTarget.serviceTypeKey] || 'clinical services').toLowerCase()} clinic for{' '}
                <strong>{setPrimaryTarget.customerLocationName}</strong>?
              </p>
              <p>This will replace the current primary clinic for this service type at this location.</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn-secondary"
                onClick={() => setSetPrimaryTarget(null)}
                disabled={setPrimaryMutation.isPending}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-primary"
                onClick={() => setPrimaryMutation.mutate(setPrimaryTarget.id)}
                disabled={setPrimaryMutation.isPending}
              >
                {setPrimaryMutation.isPending ? 'Saving...' : 'Set Primary'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
